import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Upload, Plus, Trash2, Check, AlertCircle, Lock,
  ArrowLeft, Loader2, CalendarDays, X
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = "";
const MAX_IMAGE_SIZE = 3 * 1024 * 1024;

const GuestManageSubmission = () => {
  const { submissionId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [artworks, setArtworks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saved, setSaved] = useState(false);
  const fileInputRefs = useRef({});

  const fetchSubmission = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/submissions/guest/manage/${submissionId}?token=${encodeURIComponent(token)}`);
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || 'Could not load submission'); return; }
      const data = await res.json();
      setSub(data);
      setArtworks((data.artworks || []).map(aw => ({
        title: aw.title || '', size: aw.size || '', medium: aw.medium || '',
        medium_category_id: aw.medium_category_id || '', price: aw.price?.toString() || '',
        image_urls: aw.image_urls || [], uploading: false
      })));
      setCategories(data.call?.categories || []);
      // Also fetch categories if not in call
      if (data.call?.exhibition_id) {
        const catRes = await fetch(`${API_URL}/api/judging/categories/public/${data.call.exhibition_id}`);
        if (catRes.ok) setCategories(await catRes.json());
      }
    } catch { toast.error('Failed to load submission'); } finally { setLoading(false); }
  }, [submissionId, token]);

  useEffect(() => { if (token) fetchSubmission(); else { toast.error('Access token missing'); setLoading(false); } }, [fetchSubmission, token]);

  const handleImageUpload = async (ai, files) => {
    const fileArr = Array.from(files);
    for (const file of fileArr) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error(`"${file.name}" not supported.`); return; }
      if (file.size > MAX_IMAGE_SIZE) { toast.error(`"${file.name}" too large. Max 3MB.`); return; }
    }
    const u = [...artworks]; u[ai] = { ...u[ai], uploading: true }; setArtworks(u);
    const urls = [...artworks[ai].image_urls];
    for (const file of fileArr) {
      const fd = new FormData(); fd.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/submissions/guest/upload-image`, { method: 'POST', body: fd });
        if (res.ok) { const d = await res.json(); urls.push(d.url); }
        else { const e = await res.json(); toast.error(e.detail || 'Upload failed'); }
      } catch { toast.error('Upload failed'); }
    }
    const f = [...artworks]; f[ai] = { ...artworks[ai], image_urls: urls, uploading: false }; setArtworks(f);
  };

  const removeImage = (ai, ii) => { const u = [...artworks]; u[ai].image_urls.splice(ii, 1); setArtworks(u); };
  const addArtwork = () => {
    const max = sub?.call?.max_artworks || 5;
    if (artworks.length >= max) { toast.error(`Max ${max} artworks`); return; }
    setArtworks([...artworks, { title: '', size: '', medium: '', medium_category_id: '', price: '', image_urls: [], uploading: false }]);
  };
  const removeArtwork = (i) => { if (artworks.length <= 1) return; setArtworks(artworks.filter((_, idx) => idx !== i)); };
  const updateArtwork = (i, f, v) => { const u = [...artworks]; u[i] = { ...u[i], [f]: v }; setArtworks(u); };

  const handleSave = async () => {
    for (let i = 0; i < artworks.length; i++) {
      const aw = artworks[i];
      if (!aw.title.trim()) { toast.error(`Artwork ${i+1}: Title required`); return; }
      if (!aw.size.trim()) { toast.error(`Artwork ${i+1}: Size required`); return; }
      if (!aw.medium.trim()) { toast.error(`Artwork ${i+1}: Medium required`); return; }
      if (!aw.price && aw.price !== 0) { toast.error(`Artwork ${i+1}: Price required`); return; }
      if (aw.image_urls.length === 0) { toast.error(`Artwork ${i+1}: Upload at least one image`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        artworks: artworks.map(aw => ({
          title: aw.title.trim(), size: aw.size.trim(), medium: aw.medium.trim(),
          medium_category_id: aw.medium_category_id || '', price: parseFloat(aw.price), image_urls: aw.image_urls
        }))
      };
      const res = await fetch(`${API_URL}/api/submissions/guest/edit/${submissionId}?token=${encodeURIComponent(token)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Save failed'); }
      toast.success('Submission updated!');
      setSaved(true);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red" /></div>;

  if (!sub) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="text-center"><AlertCircle size={48} className="mx-auto mb-4 text-red-400" /><h1 className="text-2xl font-serif text-stone-900 mb-2">Access Denied</h1><p className="text-stone-600">Invalid or expired access link.</p></div>
    </div>
  );

  const isLocked = sub.is_locked;
  const isEditable = sub.is_editable;
  const deadline = sub.call?.deadline ? new Date(sub.call.deadline) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="guest-manage-page">
      <SEOHead title="Manage Submission | CAFA" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Button variant="ghost" className="mb-4 text-stone-500" onClick={() => navigate('/exhibitions')} data-testid="back-btn"><ArrowLeft size={18} className="mr-1" /> Back to Exhibitions</Button>
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-stone-900">Manage Your Submission</h1>
          <p className="text-lg text-stone-600 mt-1">{sub.exhibition_title}</p>
          <p className="text-sm text-stone-500 mt-1">Submitted by <strong>{sub.guest_name}</strong> ({sub.guest_email})</p>
          {deadline && (
            <div className="flex items-center gap-2 mt-2 text-stone-700"><CalendarDays size={16} className="text-cafa-red" /><span className="text-sm">Deadline: {deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span></div>
          )}
          <div className="flex gap-2 mt-3">
            <Badge className={`${sub.status === 'submitted' ? 'bg-green-100 text-green-800' : sub.status === 'accepted' ? 'bg-blue-100 text-blue-800' : sub.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-stone-100 text-stone-700'}`} data-testid="status-badge">{sub.status}</Badge>
            {sub.payment_status === 'paid' && <Badge className="bg-green-100 text-green-800" data-testid="payment-badge">Paid</Badge>}
            {isLocked && <Badge className="bg-amber-100 text-amber-800" data-testid="locked-badge"><Lock size={12} className="mr-1" /> Locked</Badge>}
          </div>
        </div>

        {isLocked && !isEditable && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-800" data-testid="locked-notice">
            This submission is locked and cannot be edited. {sub.payment_status === 'paid' ? 'Payment has been completed.' : 'The deadline has passed.'}
          </div>
        )}

        {saved && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-center text-green-800 font-medium" data-testid="saved-notice"><Check size={18} className="inline mr-2" />Changes saved successfully!</div>
        )}

        <div className="space-y-6">
          {artworks.map((aw, i) => (
            <Card key={i} className="border-stone-200 shadow-sm" data-testid={`artwork-block-${i}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-serif text-stone-900">Artwork {i + 1}</h3>
                  {isEditable && artworks.length > 1 && <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeArtwork(i)}><Trash2 size={16} className="mr-1" /> Remove</Button>}
                </div>
                <div className="space-y-4">
                  <div><Label>Title *</Label><Input className="h-12 mt-1" value={aw.title} onChange={e => updateArtwork(i, 'title', e.target.value)} disabled={!isEditable} /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div><Label>Size *</Label><Input className="h-12 mt-1" value={aw.size} onChange={e => updateArtwork(i, 'size', e.target.value)} disabled={!isEditable} /></div>
                    <div><Label>Medium *</Label><Input className="h-12 mt-1" value={aw.medium} onChange={e => updateArtwork(i, 'medium', e.target.value)} disabled={!isEditable} /></div>
                    <div><Label>Price ($) *</Label><Input className="h-12 mt-1" type="number" value={aw.price} onChange={e => updateArtwork(i, 'price', e.target.value)} disabled={!isEditable} /></div>
                  </div>
                  {categories.length > 0 && (
                    <div>
                      <Label>Medium Category</Label>
                      <select className="w-full h-12 mt-1 border rounded-md px-3 bg-white" value={aw.medium_category_id} onChange={e => updateArtwork(i, 'medium_category_id', e.target.value)} disabled={!isEditable}>
                        <option value="">Select...</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <Label className="mb-2 block">Images</Label>
                    {aw.image_urls.length > 0 && (
                      <div className="flex flex-wrap gap-3 mb-3">
                        {aw.image_urls.map((url, j) => (
                          <div key={j} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-stone-200 group">
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            {isEditable && <button onClick={() => removeImage(i, j)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                    {isEditable && (
                      <div className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer border-stone-300 hover:border-cafa-red hover:bg-red-50/50 transition-colors"
                        onClick={() => fileInputRefs.current[i]?.click()}
                        onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleImageUpload(i, e.dataTransfer.files); }}>
                        <input ref={el => fileInputRefs.current[i] = el} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => { handleImageUpload(i, e.target.files); e.target.value = ''; }} />
                        {aw.uploading ? <Loader2 size={28} className="animate-spin text-cafa-red mx-auto" /> : <><Upload size={28} className="text-stone-400 mx-auto mb-1" /><p className="text-sm text-stone-500">Add images</p></>}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {isEditable && artworks.length < (sub?.call?.max_artworks || 5) && (
            <Button variant="outline" size="lg" className="w-full h-14 text-lg border-dashed border-2" onClick={addArtwork} data-testid="add-artwork-btn"><Plus size={20} className="mr-2" /> Add Another Artwork</Button>
          )}

          {isEditable && (
            <Button size="lg" className="w-full h-14 text-lg bg-cafa-red hover:bg-red-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-changes-btn">
              {saving ? <><Loader2 size={20} className="mr-2 animate-spin" /> Saving...</> : <><Check size={20} className="mr-2" /> Save Changes</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestManageSubmission;
